import { apiRequest, ApiError } from "./apiClient";
import type { Contract } from "../types/contract";
import { mapGeneratedContractRowToContract } from "./generatedContractService";
import { documentRemotePathFromDocumentUrl } from "./hostDocumentInfoService";

const LOG_PREFIX = "[generatedContractResolve]";

type BackendSuccessEnvelope<T> = {
  readonly success: boolean;
  readonly data: T;
  readonly message?: string;
};

export type ResolveByDocumentUrlData = {
  /** Generated contract row id (same shape as GET /generated-contracts list/detail). */
  readonly id?: string | null;
  /** Backend contract status string used for routing decisions. */
  readonly status?: string | null;
  /** Contract id if backend returns only an identifier. */
  readonly generated_contract_id?: string | null;
  readonly generatedContractId?: string | null;
  readonly contract_id?: string | null;
  readonly contractId?: string | null;
  /** Backend may include a contract row, either wrapped or as the entire data payload. */
  readonly generated_contract?: unknown;
  readonly generatedContract?: unknown;
  readonly contract?: unknown;
  readonly contract_row?: unknown;
  readonly generated_contract_row?: unknown;
  /** Allow additional fields (review summary payload, etc.). */
  readonly [key: string]: unknown;
};

export type ResolveByDocumentUrlResult =
  | {
      readonly ok: true;
      readonly status: string;
      readonly contractId: string;
      readonly contract: Contract | null;
      readonly raw: ResolveByDocumentUrlData;
    }
  | {
      readonly ok: false;
      readonly errorMessage: string;
      readonly errorCode?: "UNAUTHORIZED" | "NOT_FOUND" | "INVALID_URL" | "UNKNOWN";
    };

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

function pickContractId(raw: ResolveByDocumentUrlData): string | null {
  const v =
    raw.id ??
    raw.generated_contract_id ??
    raw.generatedContractId ??
    raw.contract_id ??
    raw.contractId;
  return isNonEmptyString(v) ? v.trim() : null;
}

function pickStatus(raw: ResolveByDocumentUrlData): string | null {
  const v = raw.status;
  return isNonEmptyString(v) ? v.trim() : null;
}

function looksLikeMappedContract(x: unknown): x is Contract {
  if (x == null || typeof x !== "object") return false;
  const r = x as Partial<Contract>;
  return (
    typeof r.id === "string" &&
    typeof r.title === "string" &&
    typeof r.type === "string" &&
    typeof r.date === "string" &&
    typeof r.status === "string"
  );
}

function looksLikeBackendGeneratedContractRow(x: unknown): boolean {
  if (x == null || typeof x !== "object") return false;
  const r = x as Record<string, unknown>;
  return (
    typeof r.id === "string" &&
    ("created_at" in r || "stored_file_id" in r || "display_name" in r)
  );
}

function pickContractPayload(raw: ResolveByDocumentUrlData): unknown {
  return (
    raw.generated_contract ??
    raw.generatedContract ??
    raw.contract ??
    raw.contract_row ??
    raw.generated_contract_row ??
    raw.generated_contract_row ??
    null
  );
}

function mapResolveError(err: unknown): ResolveByDocumentUrlResult {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) {
      return { ok: false, errorCode: "UNAUTHORIZED", errorMessage: "Please sign in again." };
    }
    if (err.status === 404) {
      return {
        ok: false,
        errorCode: "NOT_FOUND",
        errorMessage: "Contract not found for this document."
      };
    }
    if (err.status === 400) {
      return {
        ok: false,
        errorCode: "INVALID_URL",
        errorMessage: "This document URL could not be resolved."
      };
    }
    return {
      ok: false,
      errorCode: "UNKNOWN",
      errorMessage: err.message || "Unable to resolve contract."
    };
  }
  return {
    ok: false,
    errorCode: "UNKNOWN",
    errorMessage: err instanceof Error ? err.message : "Unable to resolve contract."
  };
}

export async function resolveGeneratedContractByDocumentUrl(
  documentUrl: string
): Promise<ResolveByDocumentUrlResult> {
  const url = documentUrl.trim();
  if (!url) {
    return { ok: false, errorCode: "INVALID_URL", errorMessage: "Missing document URL." };
  }

  try {
    const documentRemotePath = documentRemotePathFromDocumentUrl(url);
    if (!documentRemotePath) {
      return { ok: false, errorCode: "INVALID_URL", errorMessage: "This document URL could not be resolved." };
    }

    const response = await apiRequest(
      "/generated-contracts/resolve-by-document-url",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_remote_path: documentRemotePath })
      },
      { requiresAuth: true, redirectOnUnauthorized: true }
    );
    const json = (await response.json()) as BackendSuccessEnvelope<ResolveByDocumentUrlData> | ResolveByDocumentUrlData;
    const raw: ResolveByDocumentUrlData =
      (json as BackendSuccessEnvelope<ResolveByDocumentUrlData>)?.data ??
      (json as ResolveByDocumentUrlData);

    const contractId = pickContractId(raw) ?? (looksLikeMappedContract(raw) ? raw.id : null);
    const status = pickStatus(raw) ?? "unknown";

    if (!contractId) {
      console.warn(LOG_PREFIX, "resolve returned no contract id", { raw });
      return { ok: false, errorCode: "NOT_FOUND", errorMessage: "Contract not found for this document." };
    }

    const payload = pickContractPayload(raw) ?? raw;
    let contract: Contract | null = null;
    if (looksLikeMappedContract(payload)) {
      contract = payload;
    } else if (looksLikeBackendGeneratedContractRow(payload)) {
      // Best-effort mapping when backend returns the same row shape as GET /generated-contracts/{id}.
      contract = mapGeneratedContractRowToContract(payload as never);
    }

    return { ok: true, status, contractId, contract, raw };
  } catch (err: unknown) {
    const mapped = mapResolveError(err);
    console.warn(LOG_PREFIX, "resolve failed", mapped);
    return mapped;
  }
}
